import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useDndContext } from '@dnd-kit/core';
import {
  defaultRangeExtractor,
  type Range,
  useVirtualizer,
  type Virtualizer,
} from '@tanstack/react-virtual';
import { getProjection } from './utilities';
import { SortableTreeItem } from './SortableTreeItem';
import { TreeItem, type TreeItemRenderFn } from './TreeItem';
import type { FlattenedItem } from './types';

interface Props<T> {
  items: readonly FlattenedItem<T>[];
  indentationWidth?: number;
  renderItem: TreeItemRenderFn<T>;
  isDraggable?: boolean;
  withDepthMarkers?: boolean;
  virtualizerRef?: React.Ref<Virtualizer<HTMLElement, Element>>;
}

export default function SortableTree<T>({
  items,
  indentationWidth = 30,
  renderItem,
  isDraggable = true,
  withDepthMarkers = true,
  virtualizerRef,
}: Props<T>): JSX.Element {
  const dndContext = useDndContext();
  const activeId = (dndContext.active?.id as string) ?? null;
  const overId = (dndContext.over?.id as string) ?? null;
  const offsetLeft = dndContext.active
    ? (dndContext.active.rect.current.translated?.left ?? 0) -
      (dndContext.active.rect.current.initial?.left ?? 0)
    : 0;

  const filteredItems = useMemo(() => {
    if (activeId != null) {
      return items.filter(({ id, selected }) => id === activeId || !selected);
    }

    return items;
  }, [activeId, items]);

  const activeIndex = useMemo(
    () => filteredItems.findIndex(({ id }) => id === activeId),
    [activeId, filteredItems]
  );

  // Add the active index to the range so it is always rendered
  const rangeExtractor = useCallback(
    (range: Range) => {
      const extractedRange = defaultRangeExtractor(range);
      if (
        activeIndex >= 0 &&
        (activeIndex < extractedRange[0] ||
          activeIndex > extractedRange[extractedRange.length - 1])
      ) {
        extractedRange.push(activeIndex);
      }
      return extractedRange;
    },
    [activeIndex]
  );

  const getScrollElement = useCallback(
    () => containerRef.current?.parentElement ?? null,
    []
  );

  const estimateSize = useCallback(() => 30, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement,
    estimateSize,
    rangeExtractor,
    overscan: 10,
    gap: 1, // We set a 1px bottom margin before we virtualized this list
  });

  // Expose the virtualizer via ref
  useImperativeHandle(virtualizerRef, () => virtualizer, [virtualizer]);

  const virtualizedItems = virtualizer.getVirtualItems();

  const { measureDroppableContainers } = useDndContext();

  // Without this, animations are funky when using the move/sort buttons
  // dnd-kit only remeasures on drag/drop by default
  useEffect(
    function remeasureContainers() {
      measureDroppableContainers(
        virtualizedItems.map(({ index }) => filteredItems[index].id)
      );
    },
    [filteredItems, measureDroppableContainers, virtualizedItems]
  );

  const projected =
    isDraggable && activeId != null && overId != null
      ? getProjection(
          filteredItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth
        )
      : null;

  return (
    <div
      className={classNames(
        'tree-container',
        activeId != null && 'marching-ants'
      )}
      style={{
        height: `${virtualizer.getTotalSize()}px`,
        position: 'relative',
      }}
      ref={containerRef}
    >
      {virtualizer.getVirtualItems().map(({ index, start }) => {
        const item = filteredItems[index];
        const { id, depth } = item;
        return isDraggable ? (
          <SortableTreeItem
            key={id}
            id={id}
            value={id}
            depth={id === activeId && projected ? projected.depth : depth}
            item={item}
            renderItem={renderItem}
            withDepthMarkers={withDepthMarkers}
            top={start}
            // This allows the group items to expand when editing the name and shift the list correctly
            measureElement={virtualizer.measureElement}
          />
        ) : (
          <TreeItem
            key={id}
            value={id}
            depth={depth}
            item={item}
            renderItem={renderItem}
            withDepthMarkers={withDepthMarkers}
            top={start}
            wrapperRef={virtualizer.measureElement}
          />
        );
      })}
    </div>
  );
}
